import { createSelector } from "@reduxjs/toolkit";
import { GetMcbimSpacesApiResponse, openapi, PostMcbimItemsApiResponse } from "../../openapi";
import { IRootState } from "../app/store";
import {
  selectGetMcBimCeilingDevicesArgs,
  selectGetMcBimItemsArkArgs,
  selectGetMcBimSpacesParams,
} from "./McBimSlice";
import { selectedSpaceNumber } from "../DesignTab/designSelectors";
import MiniSearch from "minisearch";
import { IMcBimItem } from "@backend/src/schemas";
import { map } from "zod";

const EMPTY_RESPONSE: PostMcbimItemsApiResponse = [];

// ----- Basic McBIM selectors -----

export const selectHttpMcBimCeilingItems = (state: IRootState) => {
  const args = selectGetMcBimCeilingDevicesArgs(state);
  if (!args) return EMPTY_RESPONSE;
  return openapi.endpoints.postMcbimItems.select(args)(state).data ?? EMPTY_RESPONSE;
};

export const selectHttpGetMcBimArkItems = (state: IRootState) => {
  const args = selectGetMcBimItemsArkArgs(state);
  if (!args) return EMPTY_RESPONSE;
  return openapi.endpoints.postMcbimItems.select(args)(state).data ?? EMPTY_RESPONSE;
};

export const selecthttpMcBimSpaceItems = (state: IRootState) => {
  const args = selectGetMcBimSpacesParams(state);
  if (!args) return EMPTY_RESPONSE;
  return openapi.endpoints.getMcbimSpaces.select(args)(state).data ?? EMPTY_RESPONSE;
};

/** Hämtar query-resultatet (senaste i cachen) för nuvarande args */
export const selectHttpMcbimSpacesResult = (state: IRootState): GetMcbimSpacesApiResponse => {
  const args = selectGetMcBimSpacesParams(state);
  if (!args) return [];
  return openapi.endpoints.getMcbimSpaces.select(args)(state).data ?? [];
};

export const selectnrOfMcbimSpaces = createSelector([selectHttpMcbimSpacesResult], (res) => {
  return res.length;
});

export const selectActiveMcbimSpace = createSelector(
  [selectedSpaceNumber, selectHttpMcbimSpacesResult],
  (selectedNumber, mcbimSpaces) => {
    if (!selectedNumber) return null;
    return mcbimSpaces.find((space) => space.name === selectedNumber) ?? null;
  }
);

// ----- Shared state selectors -----

const selectFloorAndJob = (state: IRootState) => state.design.floorAndJob;
const selectProjectNo = (state: IRootState) => state.design.projectNo;

// Unika McBIM-space-namn (för att bygga roomNumbers-lista)
export const selectUniqueMcbimSpaceNames = createSelector(
  selectHttpMcbimSpacesResult,
  (res): string[] => {
    const data = (res as Array<{ name?: string }> | undefined) ?? [];
    const seen = new Set<string>();
    const out: string[] = [];

    for (const s of data) {
      const n = s?.name;
      if (n && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }

    return out;
  }
);

// Args för att hämta alla rooms (används av roomSelectors.ts)
export const selectGetRoomsArgs = createSelector(
  [selectUniqueMcbimSpaceNames, selectProjectNo],
  (uniqueNames, projectNo) => {
    if (uniqueNames.length === 0 || !projectNo) {
      return null;
    }
    return {
      body: {
        projectNo: projectNo,
        roomNumbers: uniqueNames,
      },
    };
  }
);

// ----- Standardrom (Malrom) -----

// Bygger args för getMcbimSingleMcbimSpace baserat på aktivt room,
// utan att importera selectActiveSingleRoom (undviker cirkulär import).
export const selectGetMcbimStandardSpaceArgs = createSelector(
  [
    selectedSpaceNumber,
    selectFloorAndJob,
    selectProjectNo,
    selectUniqueMcbimSpaceNames,
    (state: IRootState) => state,
  ],
  (selectedRoomNumber, floorAndJob, projectNo, uniqueNames, state) => {
    if (!selectedRoomNumber || !floorAndJob?.jobId || !projectNo) {
      return null;
    }

    // Samma args-form som selectGetRoomsArgs
    const roomsArgs = {
      body: {
        projectNo,
        roomNumbers: uniqueNames,
      },
    };

    const roomsHashmap = openapi.endpoints.postMultiplacerRooms.select(roomsArgs)(state).data ?? {};

    const activeRoom = (roomsHashmap as Record<string, { rfpMalRom?: unknown }>)[
      selectedRoomNumber
    ];

    if (!activeRoom || activeRoom.rfpMalRom == null) {
      return null;
    }

    return {
      projectNo,
      name: activeRoom.rfpMalRom as string,
      jobId: floorAndJob.jobId,
    };
  }
);

export const selectHttpGetMcBimStandardSpace = createSelector(
  [selectGetMcbimStandardSpaceArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) return null;
    return openapi.endpoints.getMcbimSingleMcbimSpace.select(args)(state).data ?? null;
  }
);

export const selectHttpMcBimItemsInStandardSpace = createSelector(
  [selectHttpGetMcBimStandardSpace, selectProjectNo, (state: IRootState) => state],
  (mcbimSpace, projectNo, state) => {
    if (!mcbimSpace) return [];
    const args = {
      projectNo: projectNo,
      spaceId: mcbimSpace._id as string,
    };
    return openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data ?? [];
  }
);

const selectFreeTextSearch = (state: IRootState) => state.design.freeTextSearch;

export const selectMcbimSpaceHitSetByFreeTextSearch = createSelector(
  [selecthttpMcBimSpaceItems, selectFreeTextSearch],
  (spaces: GetMcbimSpacesApiResponse, freeText: string | null | undefined): Set<string> | null => {
    const query = freeText?.trim();

    // Ingen söktext → alla rum räknas som träff
    if (!query) {
      return null;
    }

    const mini = new MiniSearch<GetMcbimSpacesApiResponse[number]>({
      fields: ["name", "longName"], // rumsnummer + rumsnamn
      storeFields: ["name", "longName"],
      idField: "name",
      searchOptions: {
        prefix: true,
      },
    });

    mini.addAll(spaces);

    const results = mini.search(query);

    // Resultat-id är nu name (idField: "name")
    return new Set(results.map((r) => r.id as string));
  }
);
